import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, formatApiError } from "../components/AuthContext";
import {
  Share2, Mail, Phone, Loader2, X as XIcon, ExternalLink, TrendingUp, Send,
  AlertTriangle,
} from "lucide-react";
import ComposeEmailDialog from "../components/ComposeEmailDialog";

const STATUSES = ["open", "contacted", "quoted", "converted", "declined", "closed"];
const STATUS_COLORS = {
  open:      "bg-stone-100 text-stone-700",
  contacted: "bg-blue-50 text-blue-700",
  quoted:    "bg-amber-50 text-amber-700",
  converted: "bg-emerald-50 text-emerald-700",
  declined:  "bg-rose-50 text-rose-700",
  closed:    "bg-stone-100 text-stone-500",
};

function money(n) {
  if (n == null || n === "") return "—";
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD",
    maximumFractionDigits: 0 }).format(n);
}
function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function Referrals() {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ status: "", type_id: "", partner_id: "" });
  const [composeCtx, setComposeCtx] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => v && q.set(k, v));
      const [r, t, p] = await Promise.all([
        axios.get(`${API_BASE}/referrals?${q.toString()}`),
        axios.get(`${API_BASE}/referral-partner-types`),
        axios.get(`${API_BASE}/referral-partners`),
      ]);
      setRows(r.data || []);
      setTypes(t.data || []);
      setPartners(p.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [f.status, f.type_id, f.partner_id]);

  const kpis = useMemo(() => {
    const open = rows.filter((r) => !["converted", "declined", "closed"].includes(r.status)).length;
    const converted = rows.filter((r) => r.status === "converted").length;
    const totalExpected = rows
      .filter((r) => r.status !== "declined" && r.status !== "closed")
      .reduce((s, r) => s + (r.expected_fee || 0), 0);
    return { total: rows.length, open, converted, totalExpected };
  }, [rows]);

  const updateStatus = async (assignmentId, status) => {
    try {
      await axios.patch(`${API_BASE}/referral-assignments/${assignmentId}`, { status });
      toast.success("Updated");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // v68.37/38 — Pre-filled intro email. Prefers a saved referral template
  // matching the partner's type; falls back to a hard-coded body.
  const openIntroEmail = async (r) => {
    if (!r.partner?.email) { toast.error("Partner has no email on file"); return; }
    try {
      const { data: lead } = await axios.get(`${API_BASE}/leads/${r.lead_id}`);
      const clientEmail = lead?.email || lead?.primary_contact_email || r.lead_email || "";
      const first = (r.partner.name || "").split(/\s+/)[0] || "";
      const vars = {
        lead_name:          lead?.name || r.lead_name || "",
        lead_email:         lead?.email || clientEmail || "",
        lead_phone:         lead?.phone || "",
        lead_notes:         lead?.notes || "",
        lead_source:        lead?.source || "",
        partner_name:       r.partner?.name || "",
        partner_first_name: first,
      };
      const render = (s) => (s || "").replace(/\{\{(\w+)\}\}/g,
        (_, k) => vars[k] != null ? vars[k] : "");

      let subject = "";
      let bodyText = "";
      try {
        const tplRes = await axios.get(
          `${API_BASE}/referral-email-templates?partner_type_id=${r.partner?.type_id || ""}`,
        );
        const tpls = tplRes.data || [];
        const preferred = tpls.find((t) => t.partner_type_id === r.partner?.type_id)
                        || tpls[0];
        if (preferred) {
          subject = render(preferred.subject_template);
          bodyText = render(preferred.body_template);
        }
      } catch { /* fall through */ }

      if (!subject || !bodyText) {
        subject = `Referral introduction — ${lead?.name || r.lead_name || "new client"}`;
        bodyText = [
          `Hi ${first || "there"},`,
          "",
          "I'd like to refer a client of ours who may benefit from your services.",
          "",
          `• Name: ${lead?.name || r.lead_name || "—"}`,
          lead?.email ? `• Email: ${lead.email}` : null,
          lead?.phone ? `• Phone: ${lead.phone}` : null,
          lead?.source ? `• Source: ${lead.source}` : null,
          lead?.notes ? `• Notes: ${lead.notes}` : null,
          "",
          clientEmail ? "I've CC'd them on this email so you can connect directly."
                       : "Please let me know the best way to introduce you.",
          "",
          "Thanks,",
        ].filter(Boolean).join("\n");
      }

      setComposeCtx({
        to: r.partner.email, cc: clientEmail,
        subject, body: bodyText, lead_id: r.lead_id,
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="px-10 py-12 max-w-[1400px]" data-testid="referrals-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="overline mb-2">Pipeline · Outgoing referrals</div>
          <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>
            Referrals <em className="italic" style={{ color: "var(--accent)" }}>tracker</em>
          </h1>
          <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
            Every lead you've sent to an external partner — mortgage brokers, valuers, insurance —
            with status, notes and expected commission tracked in one place.
          </p>
        </div>
        <Link to="/referral-partners" className="btn-secondary text-[12px]"
              data-testid="referrals-manage-directory">
          <Share2 size={12} /> Manage partners
        </Link>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-4 mb-6" data-testid="referrals-kpis">
        <KpiCard label="Total referrals" value={kpis.total} />
        <KpiCard label="Open pipeline" value={kpis.open} tone="accent" />
        <KpiCard label="Converted" value={kpis.converted} tone="moss" />
        <KpiCard label="Expected fees (open)" value={money(kpis.totalExpected)} tone="moss" />
      </div>

      {/* Filters */}
      <div className="card p-3 mb-4 flex items-center gap-3 flex-wrap"
           data-testid="referrals-filters">
        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}
                className="bg-white border border-[var(--border)] rounded px-2 py-1.5 text-[12px]"
                data-testid="referrals-filter-status">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={f.type_id} onChange={(e) => setF({ ...f, type_id: e.target.value })}
                className="bg-white border border-[var(--border)] rounded px-2 py-1.5 text-[12px]"
                data-testid="referrals-filter-type">
          <option value="">All types</option>
          {types.map((t) => <option key={t.type_id} value={t.type_id}>{t.name}</option>)}
        </select>
        <select value={f.partner_id} onChange={(e) => setF({ ...f, partner_id: e.target.value })}
                className="bg-white border border-[var(--border)] rounded px-2 py-1.5 text-[12px]"
                data-testid="referrals-filter-partner">
          <option value="">All partners</option>
          {partners.map((p) => <option key={p.partner_id} value={p.partner_id}>{p.name}</option>)}
        </select>
        {(f.status || f.type_id || f.partner_id) && (
          <button onClick={() => setF({ status: "", type_id: "", partner_id: "" })}
                  className="text-[11px] text-[var(--ink-3)] hover:text-[var(--accent)]"
                  data-testid="referrals-filter-clear">
            Clear filters
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-[var(--ink-3)]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center" data-testid="referrals-empty">
          <Share2 size={28} className="mx-auto mb-3 text-[var(--ink-3)]" />
          <div className="font-heading text-xl mb-1" style={{ fontWeight: 500 }}>
            No referrals yet
          </div>
          <p className="text-[var(--ink-3)] text-[13px]">
            Open any lead and click "Refer to…" to record a referral.
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="report-table">
            <thead>
              <tr>
                <th>Lead</th><th>Partner</th><th>Type</th><th>Status</th>
                <th>Expected fee</th><th>Commission</th><th>Assigned</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.assignment_id}
                    data-testid={`referrals-row-${r.assignment_id}`}>
                  <td>
                    <Link to={`/leads?highlight=${r.lead_id}`}
                          className="text-[var(--accent)] hover:underline text-[13px]">
                      {r.lead_name || "(deleted lead)"}
                    </Link>
                  </td>
                  <td className="text-[13px]">
                    <div className="font-medium text-[var(--ink)]">{r.partner?.name || "—"}</div>
                    {r.partner?.email && (
                      <a href={`mailto:${r.partner.email}`}
                         className="text-[11px] text-[var(--ink-3)] hover:text-[var(--accent)] inline-flex items-center gap-1">
                        <Mail size={9}/> {r.partner.email}
                      </a>
                    )}
                  </td>
                  <td className="text-[13px] text-[var(--ink-2)]">
                    {r.partner?.type_name || "—"}
                  </td>
                  <td>
                    <div className="inline-flex items-center gap-1.5">
                      <select value={r.status} onChange={(e) => updateStatus(r.assignment_id, e.target.value)}
                              className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium
                                          border-0 focus:outline-none ${STATUS_COLORS[r.status] || "bg-stone-100"}`}
                              data-testid={`referrals-status-${r.assignment_id}`}>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      {r.idle_days >= 7 && !["converted","declined","closed"].includes(r.status) && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded
                                          text-[9px] font-medium bg-amber-50 text-amber-700"
                              title={`No update in ${r.idle_days} days`}
                              data-testid={`referrals-idle-badge-${r.assignment_id}`}>
                          <AlertTriangle size={9}/> {r.idle_days}d idle
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="text-[13px] text-[var(--ink-2)]">{money(r.expected_fee)}</td>
                  <td className="text-[13px] text-[var(--ink-2)]">
                    {r.commission_percent != null ? `${r.commission_percent}%` : "—"}
                  </td>
                  <td className="text-[13px] text-[var(--ink-3)]">{fmtDate(r.assigned_at)}</td>
                  <td>
                    {r.partner?.email && (
                      <button onClick={() => openIntroEmail(r)}
                              className="text-[var(--ink-3)] hover:text-[var(--accent)]"
                              title="Send intro email to partner (CC client)"
                              data-testid={`referrals-email-${r.assignment_id}`}>
                        <Send size={12}/>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {composeCtx && (
        <ComposeEmailDialog
          open
          onClose={() => setComposeCtx(null)}
          onSent={() => { setComposeCtx(null); toast.success("Intro email sent"); }}
          context={{ lead_id: composeCtx.lead_id }}
          defaultTo={composeCtx.to}
          defaultCc={composeCtx.cc}
          defaultSubject={composeCtx.subject}
          defaultBody={composeCtx.body}
        />
      )}
    </div>
  );
}


function KpiCard({ label, value, tone }) {
  const bg = tone === "accent" ? "bg-[var(--accent-light)]/30"
           : tone === "moss"   ? "bg-[var(--moss)]/8" : "bg-white";
  return (
    <div className={`card p-4 ${bg}`}>
      <div className="overline mb-1">{label}</div>
      <div className="font-heading text-2xl" style={{ fontWeight: 500 }}>{value}</div>
    </div>
  );
}

